import React from 'react';
import { useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import AlertComponent from '../../../../utility/AlertComponent';
import IAlertType from '../../../../../utils/types/props/IAlertType';
import apiClient from '../../../../../services/api/apiClient';
import { formatAxiosError } from '../../../../../utils/formatAxiosError';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

//#region interfaces

interface ChangePasswordProps {
    setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

//#endregion

const ChangePasswordModalContent: React.FC<ChangePasswordProps> = ({ setShowModal }) => {
    //#region useState

    const [oldPassword, setOldPassword] = useState<string>();
    const [newPassword, setNewPassword] = useState<string>();
    const [pwdConfirm, setPwdConfirm] = useState<string>();

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };
    const addAlert = (type: IAlertType, message: string) => {
        setSystemAlerts((prevAlerts) => [...prevAlerts, { type, message }]);
    };

    // ---

    const { mutate, isPending: isSubmittingOperation } = useMutation({
        mutationFn: () =>
            apiClient.post('/api/compteUtilisateur/changePassword', {
                oldPassword: oldPassword,
                newPassword: newPassword,
            }),
        onSuccess: (response) => {
            if (response.data.success === true) {
                Swal.fire({
                    text: 'Opération effectuée avec succès.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    setShowModal?.(false);
                });
            } else {
                setSystemAlerts((prevAlerts) => [
                    ...prevAlerts,
                    {
                        type: 'danger',
                        message: response.data.message,
                    },
                ]);
            }
        },
        onError: (err) => {
            addAlert('danger', formatAxiosError(err)?.message ?? "Erreur lors de l'opération.");
        },
    });

    //#endregion

    //#region methods

    const submitForm = () => {
        let errorMsg: string | null = null;

        if (!oldPassword || !newPassword || !pwdConfirm) {
            errorMsg = 'Tous les champs sont requis.';
        } else if (newPassword !== pwdConfirm) {
            errorMsg = 'Le nouveau mot de passe et le mot de passe de confirmation ne correspondent pas.';
        }

        if (errorMsg) {
            toast(errorMsg);
            return;
        }

        mutate();
    };

    //#endregion

    return (
        <>
            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />

            <div>
                <form action="#">
                    <div className="row">
                        <div className="col-md-12 form-group">
                            <label>Ancien mot de passe</label>
                            <input type="password" className="form-control" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                        </div>

                        <div className="col-md-12 form-group">
                            <label>Nouveau mot de passe</label>
                            <input type="password" className="form-control" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                        </div>

                        <div className="col-md-12 form-group">
                            <label>Confirmation mot de passe</label>
                            <input type="password" className="form-control" value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)} />
                        </div>
                    </div>

                    <div className="text-end mt-3">
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={() => {
                                submitForm();
                            }}
                            disabled={isSubmittingOperation}
                        >
                            {isSubmittingOperation ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    &nbsp; Valider
                                </>
                            ) : (
                                `Valider`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

const ChangePasswordModal: React.FC<ChangePasswordProps> = () => {
    //#region useState

    // State to control modal visibility
    const [showModal, setShowModal] = useState(false);

    // Functions to toggle the modal

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    //#endregion

    // ---

    return (
        <div>
            <button type="button" className="btn btn-1" onClick={handleOpenModal}>
                Changer de mot de passe
            </button>

            {/* Modal component */}
            <Modal show={showModal} onHide={handleCloseModal} className="fade" centered scrollable backdrop="static" size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Changer de mot de passe</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ChangePasswordModalContent setShowModal={setShowModal} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ChangePasswordModal;
